import { Fragment } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  HeartHandshake,
  ClipboardList,
  Search,
  Users,
  Handshake,
  ArrowRight,
} from "lucide-react";
import { PrimaryButton, GoldButton } from "@/components/Layout";
import { Reveal, MaskedLine, ParallaxImage, ChapterNumber, Spotlight, EASE } from "@/components/motion";
import { B } from "@/components/BrandText";
import { usePageTitle } from "@/hooks/usePageTitle";

const ChapterOverline = ({ children }) => (
  <p className="text-sm tracking-[0.3em] uppercase font-semibold text-secondary">{children}</p>
);

export default function Home() {
  usePageTitle(
    "One More Shift | Experience Doesn't Retire | Starting Local South of Houston",
    "One More Shift connects businesses and individuals in the communities south and southeast of Houston with experienced people for consulting, mentoring, training, troubleshooting and project work."
  );
  const reduce = useReducedMotion();

  return (
    <div>
      <section className="bg-background relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 pt-14 md:pt-20 pb-14">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <h1
                className="text-4xl sm:text-5xl xl:text-6xl font-bold leading-[1.08] tracking-tight"
                data-testid="hero-headline"
              >
                <MaskedLine delay={0.1}>Real problems.</MaskedLine>
                <MaskedLine delay={0.25}>Real experience.</MaskedLine>
                <MaskedLine delay={0.4}>
                  <span className="text-secondary italic">Real solutions.</span>
                </MaskedLine>
              </h1>
              <motion.div
                className="mt-6 h-1 w-24 bg-secondary origin-left"
                aria-hidden="true"
                initial={reduce ? { opacity: 0 } : { scaleX: 0 }}
                animate={{ scaleX: 1, opacity: 1 }}
                transition={{ duration: 1, delay: 0.7, ease: EASE }}
              />
              <motion.p
                className="mt-6 text-lg leading-relaxed max-w-md text-foreground"
                data-testid="hero-subtext"
                initial={{ opacity: 0, y: reduce ? 0 : 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.6, ease: EASE }}
              >
                <span className="font-bold text-primary">Tell us what you're trying to solve.</span> <B />{" "}
                connects you with experienced advisors who can consult, mentor, evaluate, train or
                troubleshoot.
              </motion.p>
            </div>
            <div className="lg:col-span-3 flex flex-col gap-4">
              {[
                {
                  to: "/find-experience",
                  testId: "hero-need-experience-button",
                  icon: Search,
                  iconClass: "text-secondary",
                  bg: "bg-primary text-primary-foreground hover:bg-[#15325a]",
                  title: "FIND AN ADVISOR",
                  sub: "Get the help you need.",
                  subClass: "text-white/85",
                  delay: 0.7,
                },
                {
                  to: "/share-experience",
                  testId: "hero-have-experience-button",
                  icon: Users,
                  iconClass: "text-primary",
                  bg: "bg-secondary text-secondary-foreground hover:bg-[#caa95c]",
                  title: "BECOME AN ADVISOR",
                  sub: "Maybe you've got One More Shift.",
                  subClass: "",
                  delay: 0.8,
                },
              ].map((c) => (
                <motion.div
                  key={c.testId}
                  initial={{ opacity: 0, y: reduce ? 0 : 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: c.delay, ease: EASE }}
                >
                  <Link
                    to={c.to}
                    data-testid={c.testId}
                    className={`group flex items-center gap-4 ${c.bg} rounded-md px-6 py-5 transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2`}
                  >
                    <c.icon size={30} className={`${c.iconClass} shrink-0`} aria-hidden="true" />
                    <span className="flex-1">
                      <span className="block text-lg font-bold tracking-wide">{c.title}</span>
                      <span className={`block text-sm ${c.subClass} mt-0.5`}>{c.sub}</span>
                    </span>
                    <ArrowRight
                      size={22}
                      className="shrink-0 opacity-0 -translate-x-2 transition-[opacity,transform] duration-300 group-hover:opacity-100 group-hover:translate-x-0"
                      aria-hidden="true"
                    />
                  </Link>
                </motion.div>
              ))}
            </div>
            <motion.div
              className="lg:col-span-4 relative"
              initial={{ opacity: 0, scale: reduce ? 1 : 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            >
              <Spotlight className="-inset-12" />
              <ParallaxImage
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1600&q=80"
                alt="Two people working through a problem together over documents at a desk"
                className="relative rounded-lg border border-border h-64 sm:h-80 lg:h-[24rem]"
                imgClassName="object-center"
                testId="hero-image"
              />
            </motion.div>
          </div>
        </div>
        <div className="border-t border-border bg-card" data-testid="hero-process-strip">
          <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col gap-6 lg:flex-row lg:items-center">
            {[
              {
                icon: ClipboardList,
                title: "Tell us the problem",
                text: "Describe what you're trying to accomplish.",
                testId: "hero-strip-step-1",
              },
              {
                icon: Users,
                title: "We find the right advisor",
                text: "We match you with experience that fits your situation.",
                testId: "hero-strip-step-2",
              },
              {
                icon: Handshake,
                title: "Put experience to work",
                text: "Get the guidance, training or troubleshooting you need.",
                testId: "hero-strip-step-3",
              },
            ].map((s, i) => (
              <Fragment key={s.testId}>
                {i > 0 && (
                  <ArrowRight size={26} className="hidden lg:block text-secondary shrink-0" aria-hidden="true" />
                )}
                <Reveal delay={i * 0.12} className="flex items-start gap-4 lg:flex-1">
                  <div data-testid={s.testId} className="flex items-start gap-4">
                    <s.icon size={30} className="text-secondary shrink-0 mt-1" aria-hidden="true" />
                    <div>
                      <p className="font-bold text-primary text-base uppercase tracking-wide">{s.title}</p>
                      <p className="text-base text-foreground mt-1">{s.text}</p>
                    </div>
                  </div>
                </Reveal>
              </Fragment>
            ))}
            <div className="hidden lg:block w-px self-stretch bg-border" aria-hidden="true"></div>
            <Reveal delay={0.36} className="lg:max-w-[220px]">
              <p className="font-serif font-bold text-primary text-lg" data-testid="hero-strip-callout">
                Decades of experience.
                <br />
                Without another full-time hire.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-card border-b border-border relative overflow-hidden" data-testid="concept-section">
        <ChapterNumber className="top-4 -right-4 md:right-8">01</ChapterNumber>
        <div className="max-w-4xl mx-auto px-6 py-24 md:py-32 relative">
          <Reveal>
            <ChapterOverline>Chapter 01 — The Idea</ChapterOverline>
            <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight" data-testid="concept-headline">
              The experience you need is already out there.
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p
              className="mt-8 text-2xl md:text-3xl font-serif font-bold text-primary leading-snug"
              data-testid="home-concept-line"
            >
              Companies and individuals bring us problems. We connect them with experienced people who will be
              able to solve them.
            </p>
          </Reveal>
          <div className="mt-8 space-y-6 text-lg leading-relaxed text-foreground">
            <Reveal as="p" delay={0.1}>Years of practical knowledge don't disappear when someone leaves a full-time career.</Reveal>
            <Reveal as="p" delay={0.15}>
              In our local communities are people who have spent decades operating equipment, managing teams,
              training employees, solving production problems, caring for families, running offices, maintaining
              facilities, driving professionally, building businesses and mastering skills that can be difficult to
              learn from a manual.
            </Reveal>
            <Reveal as="p" delay={0.2}>
              Some have retired. Some have stepped away from full-time careers. Others simply have valuable
              experience they're willing to share.
            </Reveal>
            <Reveal delay={0.25}>
              <p
                className="font-semibold text-primary text-xl border-l-4 border-secondary pl-5"
                data-testid="retired-emphasis"
              >
                <B /> is being built to connect that experience with people who need it.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-background relative overflow-hidden" data-testid="fork-section">
        <ChapterNumber className="top-4 -left-4 md:left-8">02</ChapterNumber>
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 relative">
          <Reveal>
            <div className="text-center">
              <ChapterOverline>Chapter 02 — Two Ways In</ChapterOverline>
            </div>
            <h2 className="mt-4 text-3xl md:text-5xl font-bold text-center" data-testid="fork-headline">
              One network. Two ways in.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            <Reveal>
              <div
                data-testid="fork-need-card"
                className="h-full bg-primary text-primary-foreground rounded-lg p-10 flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <p className="text-secondary text-sm tracking-[0.25em] uppercase font-semibold" data-testid="fork-need-overline">
                  I need experience
                </p>
                <h3 className="mt-3 text-3xl md:text-4xl font-bold text-white">Find an Advisor</h3>
                <p className="mt-5 text-xl font-serif italic text-white/90 leading-relaxed flex-1" data-testid="fork-need-quote">
                  "I have a problem, project or need where the right experience could help."
                </p>
                <div className="mt-8">
                  <GoldButton to="/find-experience" testId="fork-find-advisor-button">
                    FIND AN ADVISOR
                  </GoldButton>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div
                data-testid="fork-have-card"
                className="h-full bg-secondary text-secondary-foreground rounded-lg p-10 flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <p className="text-primary text-sm tracking-[0.25em] uppercase font-semibold" data-testid="fork-have-overline">
                  I have experience
                </p>
                <h3 className="mt-3 text-3xl md:text-4xl font-bold text-primary">Become an Advisor</h3>
                <p className="mt-5 text-xl font-serif italic leading-relaxed flex-1" data-testid="fork-have-quote">
                  "I have knowledge and real-world experience that could help others."
                </p>
                <div className="mt-8">
                  <PrimaryButton to="/share-experience" testId="fork-become-advisor-button">
                    BECOME AN ADVISOR
                  </PrimaryButton>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-primary relative overflow-hidden" data-testid="experience-in-action-section">
        <Spotlight className="w-[60rem] h-[60rem] -top-96 left-1/2 -translate-x-1/2 opacity-[0.1]" />
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32 relative">
          <Reveal>
            <p className="text-secondary font-semibold text-lg uppercase tracking-wide text-center" data-testid="founding-network-note">
              Starting local. Built on experience.
            </p>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold text-white text-center" data-testid="experience-in-action-headline">
              Experience, passed from one person to another.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                src: "https://images.unsplash.com/photo-1771308355433-680ed06b910f?auto=format&fit=crop&w=900&q=80",
                alt: "An experienced machinist showing a younger worker how to do the job in a workshop",
                caption: "Manufacturing & industrial know-how",
                testId: "action-image-manufacturing",
              },
              {
                src: "https://images.unsplash.com/photo-1632862378103-8248dccb7e3d?auto=format&fit=crop&w=900&q=80",
                alt: "Skilled tradespeople working through a project together",
                caption: "Skilled trades, demonstrated hands-on",
                testId: "action-image-trades",
              },
              {
                src: "https://images.unsplash.com/photo-1630406144797-821be1f35d75?auto=format&fit=crop&w=900&q=80",
                alt: "A driving evaluator with a clipboard coaching a driver beside a car",
                caption: "Transportation & fleet experience",
                testId: "action-image-transportation",
              },
              {
                src: "https://images.unsplash.com/photo-1758874960380-0a094fcdc6a5?auto=format&fit=crop&w=900&q=80",
                alt: "An experienced woman teaching a younger family member in a home kitchen",
                caption: "Home, property & business expertise",
                testId: "action-image-home",
              },
            ].map((img, i) => (
              <Reveal key={img.testId} delay={i * 0.1}>
                <figure data-testid={img.testId} className="bg-white/5 border border-white/20 rounded-lg overflow-hidden">
                  <ParallaxImage src={img.src} alt={img.alt} className="w-full h-56" />
                  <figcaption className="px-4 py-3 text-white text-base font-semibold">{img.caption}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14 text-center">
            <p className="text-white/85 text-lg max-w-2xl mx-auto" data-testid="fork-footer-note">
              <HeartHandshake className="inline-block mr-2 -mt-1 text-secondary" size={24} aria-hidden="true" />
              Starting local in Pearland, Friendswood, Alvin, League City, Webster, Dickinson and the Clear Lake
              area.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
